import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Coins, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getCreditBalance } from '../services/creditApiClient';

const PaymentSuccess: React.FC = () => {
    const navigate = useNavigate();
    const { refreshCredits } = useAuth();
    const [newBalance, setNewBalance] = useState<number | null>(null);

    useEffect(() => {
        // Refresh credits when landing on success page
        const updateCredits = async () => {
            try {
                await refreshCredits();
                const balance = await getCreditBalance();
                setNewBalance(balance);
            } catch (error) {
                console.error('Failed to refresh credits:', error);
            }
        };

        updateCredits();

        // Auto redirect after 5 seconds
        const timer = setTimeout(() => {
            navigate('/dashboard');
        }, 5000);

        return () => clearTimeout(timer);
    }, [refreshCredits, navigate]);

    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4 relative overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-teal-900/20 via-[#050505] to-[#050505]" />
            <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-teal-500/20 to-transparent" />
            <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-teal-500/20 to-transparent" />

            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative z-10 max-w-md w-full bg-[#101012] border border-white/10 rounded-2xl p-8 shadow-2xl overflow-hidden"
            >
                {/* Glow Effect */}
                <div className="absolute -top-[100px] left-1/2 transform -translate-x-1/2 w-[200px] h-[200px] bg-teal-500/20 blur-[60px] rounded-full pointer-events-none" />

                <div className="text-center relative z-10">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', duration: 0.6, delay: 0.2 }}
                        className="inline-flex items-center justify-center w-24 h-24 bg-teal-500/10 rounded-full mb-6 border border-teal-500/30"
                    >
                        <Check className="w-12 h-12 text-teal-400" />
                    </motion.div>

                    <h1 className="text-3xl font-bold text-white mb-2">Payment Successful!</h1>
                    <p className="text-zinc-400 mb-8">
                        Thank you for your purchase. Your credits have been added to your account.
                    </p>

                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-8">
                        <div className="text-sm text-zinc-400 mb-1">New Balance</div>
                        <div className="flex items-center justify-center gap-2 text-2xl font-bold text-teal-400">
                            <Coins className="w-6 h-6" />
                            {newBalance !== null ? newBalance : '...'} Credits
                        </div>
                    </div>

                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => navigate('/dashboard')}
                        className="w-full py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-semibold text-lg shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 transition-all duration-300 flex items-center justify-center gap-2"
                    >
                        Go to Dashboard
                        <ArrowRight className="w-5 h-5" />
                    </motion.button>

                    <p className="text-xs text-zinc-500 mt-4">
                        Redirecting automatically in 5 seconds...
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default PaymentSuccess;
